import type React from 'react';
import { type FC, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '../../utils/ui';
import Button from '../Button/ButtonV2';
import Image, { type ImageProps } from '../Image/Image';
import Spinner from '../Spinner/Spinner';
import Text from '../Text/Text';

export interface FileError {
    code: string;
    message: string;
}

export interface DropzoneProps {
    className?: string;
    rootClassName?: string;
    maxSize?: number;
    showSelectButton?: boolean;
    showDeleteButton?: boolean;
    showInstructions?: boolean;
    hideMainArea?: boolean;
    isLoading?: boolean;
    isDisabled?: boolean;
    onFile: (file: File | string | null) => void;
    onError?: (error: FileError) => void;
    instructionsText?: string;
    selectButtonText?: string | ReactNode;
    deleteButtonText?: string;
    accept?: string | string[];
    previewImage?: string;
    previewImageProps?: Partial<ImageProps>;
    convertToBase64?: boolean;
}

const Dropzone: FC<DropzoneProps> = props => {
    const {
        isDisabled = false,
        rootClassName = '',
        showDeleteButton = false,
        showSelectButton = true,
        hideMainArea = false,
        isLoading = false,
        accept = 'image/*',
        onError,
        maxSize = Number.POSITIVE_INFINITY,
        onFile,
        className = '',
        previewImage,
        instructionsText = 'Drag & drop files here or click to select',
        selectButtonText = 'Select',
        deleteButtonText = 'Delete',
        previewImageProps = {},
        convertToBase64 = false,
    } = props;

    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string>(previewImage || '');
    const [isDragActive, setIsDragActive] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Convert accept prop to proper format for input element
    const acceptString = Array.isArray(accept) ? accept.join(',') : accept;

    // Helper function to convert file to base64
    const convertFileToBase64 = useCallback((file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }, []);

    // Cleanup preview URL on unmount
    useEffect(() => {
        return () => {
            if (typeof preview === 'string' && preview.startsWith('blob:')) {
                URL.revokeObjectURL(preview);
            }
        };
    }, [preview]);

    const validateFile = useCallback(
        (file: File): FileError | null => {
            if (file.size > maxSize) {
                return {
                    code: 'file-too-large',
                    message: `File is too large. Maximum size is ${maxSize} bytes.`,
                };
            }

            if (acceptString && acceptString !== '*') {
                const acceptedTypes = acceptString.split(',').map(type => type.trim());
                const isValidType = acceptedTypes.some(type => {
                    if (type.endsWith('/*')) {
                        const baseType = type.replace('/*', '');
                        return file.type.startsWith(baseType + '/');
                    }
                    return file.type === type || file.name.toLowerCase().endsWith(type);
                });

                if (!isValidType) {
                    return {
                        code: 'file-invalid-type',
                        message: `File type ${file.type} is not accepted.`,
                    };
                }
            }

            return null;
        },
        [maxSize, acceptString]
    );

    const handleFile = useCallback(
        async (selectedFile: File) => {
            const error = validateFile(selectedFile);
            if (error) {
                onError?.(error);
                return;
            }

            setFile(selectedFile);
            // Create preview for images
            if (selectedFile.type.startsWith('image/')) {
                const previewUrl = URL.createObjectURL(selectedFile);
                setPreview(previewUrl);
            } else {
                setPreview('');
            }

            // Convert to base64 if requested, otherwise return file
            if (convertToBase64) {
                try {
                    const base64String = await convertFileToBase64(selectedFile);
                    onFile(base64String);
                } catch (error) {
                    console.error('Failed to convert file to base64:', error);
                    onError?.({
                        code: 'conversion-error',
                        message: 'Failed to convert file to base64.',
                    });
                }
            } else {
                onFile(selectedFile);
            }
        },
        [validateFile, onError, onFile, convertToBase64, convertFileToBase64]
    );

    const handleFileInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const selectedFile = event.target.files?.[0];
            if (selectedFile) {
                handleFile(selectedFile);
            }
        },
        [handleFile]
    );

    const handleDragOver = useCallback((event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(true);
    }, []);

    const handleDragLeave = useCallback((event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(false);
    }, []);

    const handleDrop = useCallback(
        (event: React.DragEvent) => {
            event.preventDefault();
            event.stopPropagation();
            setIsDragActive(false);

            const droppedFile = event.dataTransfer.files[0];
            if (droppedFile) {
                handleFile(droppedFile);
            }
        },
        [handleFile]
    );

    const handleSelectClick = useCallback(() => {
        if (!isDisabled && !isLoading && fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, [isDisabled, isLoading]);

    const handleDeleteClick = useCallback(() => {
        if (!isDisabled) {
            setFile(null);
            if (typeof preview === 'string' && preview.startsWith('blob:')) {
                URL.revokeObjectURL(preview);
            }
            setPreview('');
            onFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [isDisabled, preview, onFile]);

    const rootClasses = cn(
        'relative flex items-center justify-center cursor-pointer transition rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 border-2 border-dashed',
        {
            'border-primary-500 bg-primary-50 dark:bg-primary-900/20': isDragActive,
            'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500': !isDragActive && !isDisabled && !preview,
            'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 cursor-not-allowed opacity-50': isDisabled,
            'border-gray-300 dark:border-gray-600': !isDragActive && !isDisabled && preview,
        },
        preview ? '' : 'p-6',
        rootClassName
    );

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleSelectClick();
            }
        },
        [handleSelectClick]
    );

    const renderMainArea = () => {
        if (hideMainArea) return null;

        return (
            <button
                type="button"
                className={rootClasses}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleSelectClick}
                onKeyDown={handleKeyDown}
                disabled={isDisabled}
                aria-label={`Drop files here or click to select. ${instructionsText}`}
            >
                <input ref={fileInputRef} type="file" accept={acceptString} onChange={handleFileInputChange} disabled={isDisabled || isLoading} className="hidden" />

                {/* Preview */}
                {preview && (
                    <div className="absolute inset-0 overflow-hidden lg:rounded-lg">
                        <Image {...previewImageProps} src={preview} className={cn('w-full h-full object-cover', previewImageProps.className)} />
                    </div>
                )}

                {/* Loading */}
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 lg:rounded-lg">
                        <Spinner />
                    </div>
                )}

                {/* Instructions */}
                {!preview && !isLoading && (
                    <div className="text-center">
                        <Text enableMultiline={true} className="text-sm text-gray-600 dark:text-gray-400">
                            {instructionsText}
                        </Text>
                    </div>
                )}
            </button>
        );
    };

    return (
        <div className={cn('w-full', className)}>
            {renderMainArea()}

            {hideMainArea && <input ref={fileInputRef} type="file" accept={acceptString} onChange={handleFileInputChange} disabled={isDisabled || isLoading} className="hidden" />}

            {/* Action Buttons */}
            {(showSelectButton || showDeleteButton) && (
                <div className="flex items-center mt-3 space-x-3">
                    {showSelectButton && (
                        <Button isLoading={isLoading} isDisabled={isDisabled} onClick={handleSelectClick} size="sm" color="base">
                            {selectButtonText}
                        </Button>
                    )}
                    {showDeleteButton && (
                        <Button isDisabled={!file && !preview} onClick={handleDeleteClick} size="sm" color="base" variant="filled">
                            <Text>{deleteButtonText}</Text>
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

export default Dropzone;
